import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { useAccounts } from "@/api/accounts";
import { useCategories } from "@/api/categories";
import { useCreateTransaction, useUpdateTransaction } from "@/api/transactions";
import { useAuth } from "@/context/AuthContext";
import { suggestCategoryName } from "@/lib/categorySuggestion";
import { notifyTransactionCreated } from "@/lib/interstitialAdTrigger";
import { todayISO, formatCurrency } from "@/lib/format";
import { ApiError } from "@/lib/apiClient";
import { cn } from "@/lib/cn";
import { CategoryStep } from "./CategoryStep";
import { AccountStep } from "./AccountStep";
import { WizardHeader } from "./WizardHeader";
import type { RecurrenceOption, Transaction, TransactionStatus } from "@/api/types";
import styles from "./TransactionFormModal.module.css";
import wizardStyles from "./wizardSteps.module.css";
import formStyles from "@/styles/form.module.css";

interface TransactionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction?: Transaction | null;
}

export function TransactionFormModal({ isOpen, onClose, transaction }: TransactionFormModalProps) {
  if (transaction) {
    return <EditTransactionModal isOpen={isOpen} onClose={onClose} transaction={transaction} />;
  }
  return <CreateTransactionWizard isOpen={isOpen} onClose={onClose} />;
}

const editSchema = z.object({
  accountId: z.string().min(1, "Selecione a conta."),
  categoryId: z.string().min(1, "Selecione uma categoria."),
  amount: z.coerce.number().positive("Informe um valor maior que zero."),
  description: z.string().trim().min(1, "Informe uma descrição."),
  date: z.string().min(1),
  status: z.enum(["paid", "pending"]),
});

type EditFormData = z.infer<typeof editSchema>;

function EditTransactionModal({
  isOpen,
  onClose,
  transaction,
}: {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction;
}) {
  const { data: accounts } = useAccounts();
  const { data: categories } = useCategories();
  const updateTransaction = useUpdateTransaction();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditFormData>({ resolver: zodResolver(editSchema) });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      accountId: transaction.accountId,
      categoryId: transaction.categoryId ?? "",
      amount: transaction.amount,
      description: transaction.description,
      date: transaction.date,
      status: transaction.status,
    });
    setSuggestion(null);
    setServerError(null);
  }, [isOpen, transaction, reset]);

  const accountId = watch("accountId");
  const categoryId = watch("categoryId");
  const type = transaction.type === "income" ? "income" : "expense";

  const filteredCategories = useMemo(() => categories?.filter((c) => c.type === type) ?? [], [categories, type]);
  const allAccounts = useMemo(() => accounts ?? [], [accounts]);
  const selectedIsCard = allAccounts.find((a) => a.id === accountId)?.type === "credit_card";

  const onSubmit = async (data: EditFormData) => {
    setServerError(null);
    try {
      await updateTransaction.mutateAsync({ id: transaction.id, data });
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : "Não foi possível salvar. Tente de novo.");
    }
  };

  const handleDescriptionChange = (value: string) => {
    const suggestedName = suggestCategoryName(value);
    if (suggestedName) {
      const match = filteredCategories.find((c) => c.name === suggestedName);
      if (match) {
        setSuggestion(match.id);
        return;
      }
    }
    setSuggestion(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar lançamento">
      <form onSubmit={handleSubmit(onSubmit)} className={formStyles.form}>
        <Input
          label="Descrição"
          placeholder="Ex: Mercado, Uber, Salário..."
          error={errors.description?.message}
          {...register("description", { onChange: (e) => handleDescriptionChange(e.target.value) })}
        />

        <div className={formStyles.row2}>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <CurrencyInput label="Valor" error={errors.amount?.message} value={field.value} onChange={field.onChange} />
            )}
          />
          <Input label="Data" type="date" error={errors.date?.message} {...register("date")} />
        </div>

        <div>
          <AccountStep
            accounts={allAccounts}
            accountId={accountId || null}
            onSelect={(id) => setValue("accountId", id, { shouldValidate: true, shouldDirty: true })}
          />
          {errors.accountId && <p className={wizardStyles.errorText}>{errors.accountId.message}</p>}
          {selectedIsCard && (
            <p className={wizardStyles.cardHint}>
              Lançamento no cartão — a fatura soma automaticamente. Quando for pagá-la, use{" "}
              <strong>"Pagar fatura"</strong> em Contas e cartões, não crie outro lançamento aqui.
            </p>
          )}
        </div>

        <div>
          <CategoryStep
            categories={filteredCategories}
            categoryId={categoryId || null}
            onSelect={(id) => setValue("categoryId", id, { shouldValidate: true, shouldDirty: true })}
          />
          {errors.categoryId && <p className={wizardStyles.errorText}>{errors.categoryId.message}</p>}
          {suggestion && suggestion !== categoryId && (
            <button type="button" onClick={() => setValue("categoryId", suggestion)} className={styles.suggestionButton}>
              Sugestão: {filteredCategories.find((c) => c.id === suggestion)?.name} (clique para aplicar)
            </button>
          )}
        </div>

        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <div className={wizardStyles.statusToggle}>
              <button
                type="button"
                onClick={() => field.onChange("paid")}
                className={cn(wizardStyles.statusOption, field.value === "paid" && wizardStyles.statusOptionPaidActive)}
              >
                Pago/Recebido
              </button>
              <button
                type="button"
                onClick={() => field.onChange("pending")}
                className={cn(wizardStyles.statusOption, field.value === "pending" && wizardStyles.statusOptionPendingActive)}
              >
                Pendente
              </button>
            </div>
          )}
        />

        {serverError && <p className={formStyles.errorText}>{serverError}</p>}
        <Button type="submit" isLoading={isSubmitting} className={formStyles.fullWidth}>
          Salvar alterações
        </Button>
      </form>
    </Modal>
  );
}

function CreateTransactionWizard({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const { data: accounts } = useAccounts();
  const { data: categories } = useCategories();
  const createTransaction = useCreateTransaction();

  const [step, setStep] = useState(0);
  const [type, setType] = useState<"income" | "expense">("expense");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState<number | "">("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [date, setDate] = useState(todayISO());
  const [status, setStatus] = useState<TransactionStatus>("paid");
  const [recurrence, setRecurrence] = useState<RecurrenceOption>("none");
  const [installmentTotal, setInstallmentTotal] = useState<number | "">(12);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    // reseta tanto ao abrir quanto ao fechar (mesmo sem salvar), pra nunca sobrar
    // valor digitado da vez anterior quando abrir de novo
    setStep(0);
    setType("expense");
    setDescription("");
    setAmount("");
    setCategoryId(null);
    setAccountId(null);
    setDate(todayISO());
    setStatus("paid");
    setRecurrence("none");
    setInstallmentTotal(12);
    setSaveError(null);
  }, [isOpen]);

  const filteredCategories = useMemo(() => categories?.filter((c) => c.type === type) ?? [], [categories, type]);
  const allAccounts = useMemo(() => accounts ?? [], [accounts]);
  const selectedIsCard = allAccounts.find((a) => a.id === accountId)?.type === "credit_card";

  const handleTypeChange = (t: "income" | "expense") => {
    setType(t);
    setCategoryId(null);
  };

  const detailsValid = description.trim() !== "" && amount !== "" && Number(amount) > 0;

  const handleContinueFromDetails = () => {
    // Já pré-seleciona a categoria mais provável (mesma lógica usada na sugestão do form
    // clássico) - a pessoa só precisa confirmar tocando, em vez de procurar do zero.
    const suggestedName = suggestCategoryName(description);
    if (suggestedName) {
      const match = filteredCategories.find((c) => c.name === suggestedName);
      if (match) setCategoryId(match.id);
    }
    setStep(1);
  };

  const handleSelectCategory = (id: string) => {
    setCategoryId(id);
    setTimeout(() => setStep(2), 260);
  };

  const handleSelectAccount = (id: string) => {
    setAccountId(id);
    setTimeout(() => setStep(3), 260);
  };

  const canSave = detailsValid && !!categoryId && !!accountId && !!date;

  const handleSave = async () => {
    if (!categoryId || !accountId || amount === "") return;
    setSaveError(null);
    try {
      await createTransaction.mutateAsync({
        type,
        accountId,
        categoryId,
        amount: Number(amount),
        description: description.trim(),
        date,
        status,
        recurrence,
        installmentTotal: recurrence === "installments" ? Number(installmentTotal) : undefined,
      });
      notifyTransactionCreated(!!user?.isPremium);
      onClose();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Não consegui salvar o lançamento.");
    }
  };

  const title = ["Novo lançamento", "Categoria", "Conta", "Finalizar"][step];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className={wizardStyles.review}>
        <WizardHeader step={step} totalSteps={4} onBack={step > 0 ? () => setStep((s) => s - 1) : undefined} />

        {step > 0 && (
          <p className={wizardStyles.previewLine}>
            {formatCurrency(Number(amount) || 0)} · {description || "—"}
          </p>
        )}

        {step === 0 && (
          <div className={wizardStyles.stepBlock}>
            <div className={wizardStyles.typeToggle}>
              <button
                type="button"
                onClick={() => handleTypeChange("expense")}
                className={cn(wizardStyles.typeButton, type === "expense" && wizardStyles.typeButtonExpenseActive)}
              >
                Despesa
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange("income")}
                className={cn(wizardStyles.typeButton, type === "income" && wizardStyles.typeButtonIncomeActive)}
              >
                Receita
              </button>
            </div>
            <Input
              label="Descrição"
              placeholder="Ex: Mercado, Uber, Salário..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <CurrencyInput
              label="Valor"
              value={amount === "" ? undefined : amount}
              onChange={(v) => setAmount(v ?? "")}
            />
            <Button type="button" onClick={handleContinueFromDetails} disabled={!detailsValid} className={formStyles.fullWidth}>
              Continuar
            </Button>
          </div>
        )}

        {step === 1 && <CategoryStep categories={filteredCategories} categoryId={categoryId} onSelect={handleSelectCategory} />}

        {step === 2 && <AccountStep accounts={allAccounts} accountId={accountId} onSelect={handleSelectAccount} />}

        {step === 3 && (
          <div className={wizardStyles.stepBlock}>
            <h3 className={wizardStyles.stepTitle}>Últimos detalhes</h3>
            <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            {selectedIsCard && (
              <p className={wizardStyles.cardHint}>
                Lançamento no cartão — a fatura soma automaticamente. Quando for pagá-la, use{" "}
                <strong>"Pagar fatura"</strong> em Contas e cartões, não crie outro lançamento aqui.
              </p>
            )}
            <div className={wizardStyles.statusToggle}>
              <button
                type="button"
                onClick={() => setStatus("paid")}
                className={cn(wizardStyles.statusOption, status === "paid" && wizardStyles.statusOptionPaidActive)}
              >
                Pago/Recebido
              </button>
              <button
                type="button"
                onClick={() => setStatus("pending")}
                className={cn(wizardStyles.statusOption, status === "pending" && wizardStyles.statusOptionPendingActive)}
              >
                Pendente
              </button>
            </div>
            <Select label="Recorrência" value={recurrence} onChange={(v) => setRecurrence(v as RecurrenceOption)}>
              <option value="none">Lançamento único</option>
              <option value="fixed">Fixo mensal (12 meses)</option>
              <option value="installments">Parcelado</option>
            </Select>
            {recurrence === "installments" && (
              <Input
                label="Número de parcelas"
                type="number"
                min={2}
                max={60}
                value={installmentTotal}
                onChange={(e) => setInstallmentTotal(e.target.value === "" ? "" : Number(e.target.value))}
              />
            )}
            {saveError && <p className={wizardStyles.errorText}>{saveError}</p>}
            <Button
              type="button"
              onClick={handleSave}
              isLoading={createTransaction.isPending}
              disabled={!canSave}
              className={formStyles.fullWidth}
            >
              Salvar lançamento
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
